/**
 * Centralized sanitize utilities — safe subset only (no data impact).
 * Used by React components only (vanilla migration is handled separately).
 *
 * - escapeHtml(): escape &, <, >, ", ' consistently (TableCellRender.js missed ').
 * - sanitizeUrl(): block javascript:, data:, vbscript:, file: — based on AccountsCards.jsx:45 allowlist.
 */

/**
 * Escape HTML special characters consistently.
 * Covers &, <, >, ", ' — all existing implementations miss ' in places.
 * @param {unknown} text
 * @returns {string}
 */
export function escapeHtml(text) {
  if (text == null || text === '') return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Attribute-safe escaping (escAttr) — same as escapeHtml but explicit for attributes.
 * Alias for clarity in call sites that build innerHTML attributes.
 */
export function escapeAttr(text) {
  return escapeHtml(text);
}

/**
 * Sanitize a user-supplied URL for safe href/src use.
 * - Blocks javascript:, data:, vbscript:, file: (case-insensitive, leading whitespace tolerant)
 * - Allows safe schemes: http, https, tel, mailto (per security guidelines)
 * - Allows blob: for same-origin object URLs (e.g. local avatar file preview)
 * - If URL has any other scheme (e.g. ftp:) → blocked (return '')
 * - If URL has no scheme → prefix with https:// (same as AccountsCards.jsx:45 websiteHref)
 * - Trims whitespace; empty → ''
 *
 * Based on AccountsCards.jsx:45 websiteHref pattern, extended to allow tel/mailto.
 *
 * @param {unknown} url
 * @returns {string} sanitized URL or '' if blocked
 */
export function sanitizeUrl(url) {
  const raw = String(url ?? '').trim();
  if (!raw) return '';
  if (/^\s*(javascript|data|vbscript|file):/i.test(raw)) return '';
  if (/^blob:/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^mailto:/i.test(raw)) {
    // Basic mailto check — block CRLF injection
    if (/[\r\n]/.test(raw)) return '';
    return raw;
  }
  if (/^tel:/i.test(raw)) {
    // tel: should only contain digits, +, -, (), space
    const after = raw.slice(4);
    if (/[\r\n]/.test(after)) return '';
    return raw;
  }
  // Root-relative paths (API-hosted avatars/assets)
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  // Any other scheme → block (e.g. ftp:, javascript obfuscation)
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '';
  // No scheme → treat as website and force https://
  return `https://${raw}`;
}

/**
 * Website-specific helper — same as sanitizeUrl but strips display? Keeps sanitizeUrl semantics.
 * Provided for semantic parity with AccountsCards websiteHref.
 */
export function sanitizeWebsiteUrl(url) {
  return sanitizeUrl(url);
}

/**
 * Tel helper — strip non-digit/+ and return tel: URL or ''.
 * Mirrors AccountsCards telHref sanitization.
 */
export function sanitizeTelUrl(phone) {
  const digits = String(phone ?? '').replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
}
